import { queryOptions } from "@tanstack/react-query";
import { getAPI } from "../../../../api/ApiHelpers.ts";
import { store } from "../../../../redux/store.ts";

export type SourcingAccessStatus = "trial" | "active" | "cancelled_grace" | "expired" | "no_plan";

export interface SourcingAccessResponse {
  status: SourcingAccessStatus;
  // Raw v2 status before mapping (payment_failed, run_down, …) — only set
  // for seat-based companies; its presence doubles as the "is v2" signal.
  platform_status?: string;
  plan?: string;
  plan_start_date?: string;
  plan_ended_date?: string;
  credits_remaining?: number;
  credits_per_cycle?: number;
  days_remaining?: number;
  seats?: number;
  seats_total?: number;
  seats_used?: number;
  admins?: number;
  max_admins?: number;
}

// Seat-based (billing_version 2) companies read the company-level
// billing/access endpoint; its extra statuses map onto the existing UI
// states so every consumer renders unchanged: payment_failed keeps access
// while Razorpay retries the charge; run_down is the post-cancellation
// credit tail (credits usable, resubscribe CTA).
const PLATFORM_STATUS_MAP: Record<string, SourcingAccessStatus> = {
  payment_failed: "active",
  run_down: "cancelled_grace",
};

const isSeatBased = () => store.getState()?.user?.user?.billing_version === 2;

const getSourcingAccess = async (slug: string): Promise<SourcingAccessResponse> => {
  if (isSeatBased()) {
    const response = await getAPI("billing/access", slug);
    if (response?.status) {
      const data = response?.data ?? {};
      return {
        ...data,
        platform_status: data.status,
        status: PLATFORM_STATUS_MAP[data.status] ?? data.status,
      };
    }
    return { status: "no_plan" };
  }
  const response = await getAPI("sourcing/access", slug);
  if (response?.status) return response?.data;
  return { status: "no_plan" };
};

const createSourcingAccessQuery = (slug: string) =>
  queryOptions({
    queryKey: ["sourcing-access", slug],
    queryFn: () => getSourcingAccess(slug),
    retry: false,
  });

export default createSourcingAccessQuery;
